import { getDateInRequestFormat } from 'utils/dateUtil';
import { DirectSalesMediaType } from 'components/common/types/DirectSalesCampaign.types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { FileList, FileContent } from 'components/common/types';
import {
  AvailabilityReportFilter,
  MandatoryAvailabilityReportFilter,
  OptionalAvailabilityReportFilter,
} from './AvailabilityReport.types';
import { LocationItem, FormatItem } from './AddFilter/AddFilter.types';

type LocationFilters = {
  includeCountryCodes?: string[];
  includeCountyCodes?: string[];
  includeDistrictCodes?: string[];
  includeTownCodes?: string[];
  includePostcodes?: string[];
  addresses?: string[];
};

type FormatFilters = {
  includeFrameSizeFamilyCodes?: string[];
  includeFrameSizeCodes?: string[];
  includeBusinessAreaCodes?: string[];
  includeRevenueStreamCodes?: string[];
  includeProductFormatCodes?: string[];
};

const LOCATION_PARENT_CODE_MAP: Record<string, keyof LocationFilters> = {
  country: 'includeCountryCodes',
  county: 'includeCountyCodes',
  district: 'includeDistrictCodes',
  town: 'includeTownCodes',
  postcode: 'includePostcodes',
  address: 'addresses',
  street: 'addresses',
};

export const transformLocationsToFilters = (locations: LocationItem[]): LocationFilters => {
  const locationFilters = locations.reduce<LocationFilters>((acc, location) => {
    const { code, parentCode } = location;
    const filterKey = parentCode ? LOCATION_PARENT_CODE_MAP[parentCode] : undefined;

    if (filterKey) {
      if (!acc[filterKey]) {
        acc[filterKey] = [];
      }
      acc[filterKey]!.push(code);
    }

    return acc;
  }, {});

  return Object.fromEntries(
    Object.entries(locationFilters).filter(([, value]) => value && value.length > 0),
  ) as LocationFilters;
};

const FORMAT_PARENT_CODE_MAP: Record<string, keyof FormatFilters> = {
  frameSizeFamily: 'includeFrameSizeFamilyCodes',
  frameSize: 'includeFrameSizeCodes',
  businessArea: 'includeBusinessAreaCodes',
  revenueStream: 'includeRevenueStreamCodes',
  productFormat: 'includeProductFormatCodes',
};

export const transformFormatsToFilters = (formats: FormatItem[]): FormatFilters => {
  const formatFilters = formats.reduce<FormatFilters>((acc, format) => {
    const { code, parentCode } = format;
    const filterKey = parentCode ? FORMAT_PARENT_CODE_MAP[parentCode] : undefined;

    if (filterKey) {
      if (!acc[filterKey]) {
        acc[filterKey] = [];
      }
      acc[filterKey]!.push(code);
    }

    return acc;
  }, {});

  return Object.fromEntries(
    Object.entries(formatFilters).filter(([, value]) => value && value.length > 0),
  ) as FormatFilters;
};

const getIncludedFramesFromUploadedFiles = (uploadedFrameLists: FileList[]): FileContent[] => {
  return uploadedFrameLists.flatMap((file) => {
    if (file.include === false) {
      return [];
    }

    const fileContents = file.fileContents || [];
    const excludedFrameCodes = file.excludedFrameCodes || [];

    return fileContents.filter((frame) => !excludedFrameCodes.includes(frame.code));
  });
};

const transformFrameListToFrameIds = (
  frameList: { routeFrameCodes?: RouteFrameCode[]; uploadedFrameLists?: FileList[] } | undefined,
  market: string,
): Array<{ assetId: string; mediaOwner: string }> | undefined => {
  if (!frameList) {
    return undefined;
  }

  const manualFrames = frameList.routeFrameCodes || [];
  const uploadedFrames = getIncludedFramesFromUploadedFiles(frameList.uploadedFrameLists || []);

  const allFrames = [...manualFrames, ...uploadedFrames];
  const frameIds = allFrames.map((frame) => ({
    assetId: frame.code,
    mediaOwner: frame.mediaOwner || market,
  }));

  return frameIds.length > 0 ? frameIds : undefined;
};

export const transformAvailabilityRequestPayload = (
  selectedFilters: AvailabilityReportFilter,
  market: string,
): {
  filter: MandatoryAvailabilityReportFilter &
    OptionalAvailabilityReportFilter &
    LocationFilters &
    FormatFilters & { networkCodes?: string[]; includeFrameIds?: Array<{ id: string; market: string }> };
} | null => {
  const { startDate, endDate, channels, mediaType, locations, formats, networks, frameList } = selectedFilters;

  if (!startDate || !endDate || !channels || !mediaType) {
    return null;
  }

  const locationFilters = locations?.length ? transformLocationsToFilters(locations) : {};

  const formatFilters = formats?.length ? transformFormatsToFilters(formats) : {};

  const networkCodes = networks?.length ? networks.map((network) => network.code) : undefined;
  const includeFrameIds = transformFrameListToFrameIds(frameList, market);

  const filter: MandatoryAvailabilityReportFilter &
    OptionalAvailabilityReportFilter &
    LocationFilters &
    FormatFilters & { networkCodes?: string[]; includeFrameIds?: Array<{ id: string; market: string }> } = {
    market,
    assetTypes: [mediaType === DirectSalesMediaType.PAPER ? ('STATIC' as DirectSalesMediaType) : mediaType],
    startDate: getDateInRequestFormat(startDate, [0, 0, 0, 0]),
    endDate: getDateInRequestFormat(endDate, [23, 59, 59, 999]),
    channelCodes: channels?.map((channel) => channel.code),
    ...locationFilters,
    ...formatFilters,
    ...(networkCodes && { networkCodes }),
    ...(includeFrameIds && { assetIds: includeFrameIds }),
  };

  return { filter };
};
